import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getDownloadURL, getMetadata, listAll, ref } from "firebase/storage";
import { db, storage } from "../firebase";

const emptyContent = { team: [], partners: [], services: [], photos: [] };

// Public, read-only mirror of the admin panel's published site document.
export default function useSiteContent() {
  const [content, setContent] = useState(emptyContent);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "siteContent", "main"),
      (snapshot) => {
        if (snapshot.exists()) {
          const remote = snapshot.data();
          setContent((current) => {
            const remotePhotos = Array.isArray(remote.photos) ? remote.photos : [];
            const remoteUrls = new Set(remotePhotos.map((photo) => photo.image));
            return {
              ...current,
              ...remote,
              photos: [...remotePhotos, ...current.photos.filter((photo) => !remoteUrls.has(photo.image))],
            };
          });
        }
      },
      () => undefined
    );

    return unsubscribe;
  }, []);

  useEffect(() => {
    let active = true;
    const loadGallery = async () => {
      try {
        const result = await listAll(ref(storage, "gallery"));
        const galleryPhotos = await Promise.all(result.items.map(async (itemRef) => {
          const [image, metadata] = await Promise.all([
            getDownloadURL(itemRef),
            getMetadata(itemRef).catch(() => ({ customMetadata: {} })),
          ]);
          const custom = metadata.customMetadata || {};
          return {
            id: `storage-${itemRef.fullPath}`,
            image,
            title: custom.title || itemRef.name.replace(/-\d+-/, "").replace(/\.[^/.]+$/, ""),
            category: custom.category || "Gallery",
          };
        }));

        if (active && galleryPhotos.length) {
          setContent((current) => {
            const existing = new Set(current.photos.map((photo) => photo.image));
            return {
              ...current,
              photos: [...current.photos, ...galleryPhotos.filter((photo) => !existing.has(photo.image))],
            };
          });
        }
      } catch {
        // Keep siteContent photos and bundled fallback content available.
      }
    };

    loadGallery();
    return () => { active = false; };
  }, []);

  return content;
}
